import { useState, useEffect } from 'react';
import { LoginPage } from './components/LoginPage';
import { SuperAdminDashboard } from './components/SuperAdminDashboard';
import { getAuthToken } from '../config/api';
import { ThemeProvider } from '../contexts/ThemeContext';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';
import { RefreshCw } from 'lucide-react';
import React from 'react';

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Register Service Worker for PWA
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          logger.info('[Service Worker] Registered successfully:', registration.scope);

          // Check for updates periodically
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  logger.info('[Service Worker] New version available.');
                  setUpdateAvailable(true);
                }
              });
            }
          });
        })
        .catch((error) => {
          logger.error('[Service Worker] Registration failed', error);
        });
    }

    // Handle back button in standalone mode to prevent navigation outside app
    const checkStandalone = () => {
      // Check if app is running in standalone mode
      return (
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true ||
        document.referrer.includes('android-app://')
      );
    };

    if (checkStandalone()) {
      // Track if we have internal navigation history
      let internalHistoryDepth = 0;
      const appOrigin = window.location.origin;

      // Push initial state to track depth
      window.history.pushState({ depth: 0, isInternal: true }, '', window.location.href);
      internalHistoryDepth = 1;

      const handlePopState = (event: PopStateEvent) => {
        // Always ensure we're within app origin
        if (!window.location.href.startsWith(appOrigin)) {
          // Prevent navigation outside app - redirect back to app
          event.preventDefault();
          window.history.pushState(null, '', '/login');
          window.location.href = '/login';
          return;
        }

        // Check if we're trying to go back beyond app entry point
        const state = event.state as { depth?: number; isInternal?: boolean } | null;

        // If no internal history or at the entry point, prevent further back navigation
        if (!state?.isInternal || internalHistoryDepth <= 1) {
          // Keep user at current location - prevent going back outside app
          event.preventDefault();
          window.history.pushState(
            { depth: 0, isInternal: true },
            '',
            window.location.pathname || '/login'
          );
          internalHistoryDepth = 1;
        } else {
          // Valid internal navigation
          internalHistoryDepth = Math.max(0, internalHistoryDepth - 1);
        }
      };

      // Override pushState to track internal navigation
      const originalPushState = window.history.pushState;
      window.history.pushState = function (...args) {
        const state = args[0] || {};
        if (window.location.href.startsWith(appOrigin)) {
          internalHistoryDepth++;
          originalPushState.call(
            window.history,
            { ...state, depth: internalHistoryDepth, isInternal: true },
            args[1] || '',
            args[2] || window.location.href
          );
        } else {
          originalPushState.apply(window.history, args);
        }
      };

      // Listen for back button
      window.addEventListener('popstate', handlePopState);

      // Cleanup
      return () => {
        window.removeEventListener('popstate', handlePopState);
        window.history.pushState = originalPushState;
      };
    }
  }, []);

  useEffect(() => {
    // Check for existing session on mount
    const checkSession = async () => {
      try {
        // Get current session from Supabase
        const { data: { session }, error } = await supabase.auth.getSession();

        if (session && session.access_token) {
          // Session exists, store it in localStorage for consistency
          localStorage.setItem('sb-auth-token', JSON.stringify({
            access_token: session.access_token,
            refresh_token: session.refresh_token,
          }));
          setIsAuthenticated(true);
        } else {
          // No valid session, check if we have a stored token
          const token = getAuthToken();
          if (token) {
            // Try to refresh the session using stored token
            try {
              const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.setSession({
                access_token: token,
                refresh_token: localStorage.getItem('sb-auth-token') ? JSON.parse(localStorage.getItem('sb-auth-token')!).refresh_token : '',
              });

              if (refreshedSession && !refreshError) {
                localStorage.setItem('sb-auth-token', JSON.stringify({
                  access_token: refreshedSession.access_token,
                  refresh_token: refreshedSession.refresh_token,
                }));
                setIsAuthenticated(true);
              } else {
                // Invalid token, clear it
                localStorage.removeItem('sb-auth-token');
                setIsAuthenticated(false);
              }
            } catch {
              // Failed to refresh, clear invalid token
              localStorage.removeItem('sb-auth-token');
              setIsAuthenticated(false);
            }
          } else {
            setIsAuthenticated(false);
          }
        }
      } catch (error) {
        logger.error('Error checking session', error);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Listen for auth state changes (auto-login on token refresh, logout on sign out)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      logger.debug('Auth state changed:', event, session ? 'Session exists' : 'No session');

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (session && session.access_token) {
          localStorage.setItem('sb-auth-token', JSON.stringify({
            access_token: session.access_token,
            refresh_token: session.refresh_token,
          }));
          setIsAuthenticated(true);
        }
      } else if (event === 'SIGNED_OUT') {
        localStorage.removeItem('sb-auth-token');
        setIsAuthenticated(false);
      }
    });

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleLoginSuccess = (token: string) => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      logger.error('Error signing out', error);
    }
    localStorage.removeItem('sb-auth-token');
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-amber-50 text-xl mb-4">Loading admin dashboard...</div>
          <div className="w-16 h-16 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      {updateAvailable && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom duration-500">
          <div className="flex items-center gap-3 bg-zinc-800 border border-amber-500/40 rounded-2xl shadow-2xl shadow-black/50 px-5 py-3.5">
            <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse flex-shrink-0" />
            <span className="text-sm text-zinc-200">Update available</span>
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
            <button
              onClick={() => setUpdateAvailable(false)}
              className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors ml-1"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}
      {!isAuthenticated ? (
        <LoginPage onLoginSuccess={handleLoginSuccess} />
      ) : (
        <SuperAdminDashboard onLogout={handleLogout} />
      )}
    </ThemeProvider>
  );
}
